import api from './api';

export interface TarionDocument {
  _id: string;
  workspaceId: string;
  projectId: string;
  fileName: string;
  url: string;
  path: string;
  size: number;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed';
  processingError?: string;
  extractedData?: any;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
}

export interface TarionDocumentsResponse {
  documents: TarionDocument[];
  total: number;
}

export interface UploadTarionDocumentResponse {
  message: string;
  document: TarionDocument;
}

export interface ProcessDocumentResponse {
  message: string;
  document: TarionDocument;
}

export const tarionDocumentService = {
  /**
   * Upload a Tarion PDF document with progress tracking
   */
  upload: async (
    workspaceId: string,
    projectId: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<TarionDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);

    const response = await api.post<UploadTarionDocumentResponse>(
      `/workspaces/${workspaceId}/tarion-documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      }
    );

    return response.data.document;
  },

  /**
   * Get all Tarion documents for a workspace and project
   */
  getAll: async (workspaceId: string, projectId?: string): Promise<TarionDocument[]> => {
    const params = projectId ? { projectId } : {};
    const response = await api.get<TarionDocumentsResponse>(
      `/workspaces/${workspaceId}/tarion-documents`,
      { params }
    );
    return response.data.documents;
  },

  /**
   * Get a single Tarion document by ID
   */
  getById: async (workspaceId: string, documentId: string): Promise<TarionDocument> => {
    const response = await api.get<{ document: TarionDocument }>(
      `/workspaces/${workspaceId}/tarion-documents/${documentId}`
    );
    return response.data.document;
  },

  /**
   * Process a Tarion document with Claude AI
   */
  process: async (workspaceId: string, documentId: string): Promise<TarionDocument> => {
    const response = await api.post<ProcessDocumentResponse>(
      `/workspaces/${workspaceId}/tarion-documents/${documentId}/process`
    );
    return response.data.document;
  },

  /**
   * Process multiple Tarion documents in bulk
   */
  processBulk: async (workspaceId: string, documentIds: string[]): Promise<{
    successful: TarionDocument[];
    failed: Array<{ documentId: string; error: string }>;
  }> => {
    const response = await api.post<{
      successful: TarionDocument[];
      failed: Array<{ documentId: string; error: string }>;
    }>(
      `/workspaces/${workspaceId}/tarion-documents/bulk-process`,
      { documentIds }
    );
    return response.data;
  },

  /**
   * Delete a Tarion document
   */
  delete: async (workspaceId: string, documentId: string): Promise<void> => {
    await api.delete(`/workspaces/${workspaceId}/tarion-documents/${documentId}`);
  }
};
